import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDismissable } from './useDismissable';
import {
  AvailableDates,
  MONTH_NAMES,
  MonthYear,
  formatMonthYear,
  sortedMonthYears,
} from './availableMonths';

interface MonthYearFieldProps {
  id: string;
  value: MonthYear | null;
  onChange: (value: MonthYear) => void;
  availableDates: AvailableDates | null;
  isLoading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  errorId?: string;
  placeholder?: string;
}

/**
 * Departure month picker driven by the availability map the packages API
 * returns for an origin/destination pair — only offered months are selectable.
 */
export const MonthYearField: React.FC<MonthYearFieldProps> = ({
  availableDates,
  disabled = false,
  errorId,
  hasError = false,
  id,
  isLoading = false,
  onChange,
  placeholder = 'Seleccioná fecha',
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismissable(isOpen, containerRef, close);

  const byYear = useMemo(() => {
    return sortedMonthYears(availableDates).reduce<Map<number, number[]>>((acc, item) => {
      acc.set(item.year, [...(acc.get(item.year) || []), item.month]);
      return acc;
    }, new Map());
  }, [availableDates]);

  const isDisabled = disabled || isLoading || byYear.size === 0;
  const triggerText = isLoading ? 'Buscando fechas…' : formatMonthYear(value) || placeholder;

  return (
    <div className='searcher-field__anchor' ref={containerRef}>
      <button
        aria-describedby={hasError && errorId ? errorId : undefined}
        aria-expanded={isOpen}
        aria-haspopup='dialog'
        className={['searcher-field__control', hasError && 'searcher-field__control--invalid']
          .filter(Boolean)
          .join(' ')}
        disabled={isDisabled}
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        type='button'
      >
        <span
          className={[
            'searcher-field__control-text',
            !value && 'searcher-field__control-text--placeholder',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {triggerText}
        </span>
      </button>

      {isOpen && (
        <div
          aria-label='Mes de salida'
          className='searcher-popover searcher-popover--panel'
          role='dialog'
        >
          {[...byYear.entries()].map(([year, months]) => (
            <div className='searcher-months__year' key={year}>
              <span className='searcher-months__year-label'>{year}</span>
              <div className='searcher-months__grid'>
                {months.map((month) => {
                  const isSelected = value?.month === month && value?.year === year;

                  return (
                    <button
                      aria-pressed={isSelected}
                      className={[
                        'searcher-months__month',
                        isSelected && 'searcher-months__month--selected',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      key={month}
                      onClick={() => {
                        onChange({ month, year });
                        close();
                      }}
                      type='button'
                    >
                      {MONTH_NAMES[month - 1]?.slice(0, 3) ?? month}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
